import React, { ChangeEvent, FC, useCallback, useEffect, useReducer, useRef, useState } from "react";
import _ from "lodash";
import ol from "openlayers";
import mapUtils, { Style } from "../../utils/map";
import gps from "../../utils/gps";
import config from "../../config";

import ListPanel from "./components/ListPanel";
import ListFilter from "./components/ListFilter";
import ListTable from "./components/ListTable";
import ListImages from "./components/ListImages";
import { Field } from "../common/form/FieldHandler";
import { useMap } from "../../App";
import { FilterItem, useFilterItem, useSetMapClickEnabled } from "../../state";
import { ImageObject } from "../../hooks/database/types/inventories";

interface Formdata {
  form: string;
  values: Record<string, Field>;
}
export interface ListFeature {
  id: string;
  coord: ol.Coordinate;
  formdata: Formdata[];
  title: string;
  olFeature: ol.Feature & { formdata?: Formdata[] };
  image?: ImageObject;
}

const compare = (haystack: any | string, needle: any | string) => {
  if (!haystack) {
    return false;
  }
  const haystackTrimmed = haystack.toString().trim().toUpperCase();
  const needleTrimmed = needle.toString().trim().toUpperCase();
  return haystackTrimmed.includes(needleTrimmed);
};

function useGPSPosition(map: ol.Map) {
  const [position, setPosition] = useState<undefined | ol.Coordinate>(undefined);

  const handleChange = useCallback(() => setPosition(gps.geolocation?.getPosition()), []);

  useEffect(() => {
    gps.init(map);
    handleChange();
    gps.geolocation!.on("change", handleChange);
    return () => gps.geolocation!.un("change", handleChange);
  }, [handleChange, map]);

  return gps.geolocation?.getTracking() ? position : undefined;
}

function debounce<T extends (...args: any) => any>(func: T, timeout: number = 300) {
  let waiting = false;
  return (...args: Parameters<T>) => {
    if (waiting) {
      return;
    }
    waiting = true;
    setTimeout(() => {
      func(...args);
      waiting = false;
    }, timeout);
  };
}

function useMapCenter(map: ol.Map) {
  const [center, setCenter] = useState(map.getView().getCenter());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChange = useCallback(
    debounce(() => setCenter(map.getView().getCenter()), 150),
    [map],
  );

  useEffect(() => {
    map.getView().on(["change:center", "change:resolution", "change:rotation"], handleChange);
    return () => map.getView().un(["change:center", "change:resolution", "change:rotation"], handleChange);
  }, [map, handleChange]);

  return center;
}

function useSelectionInteraction(map?: ol.Map, opts?: olx.interaction.SelectOptions) {
  const selectInteractionRef = useRef<ol.interaction.Select | null>(null);

  useEffect(() => {
    // Function to initialize the interaction
    function initializeInteraction() {
      if (!selectInteractionRef.current && map) {
        selectInteractionRef.current = new ol.interaction.Select(opts);
      }

      if (map && selectInteractionRef.current) {
        map.addInteraction(selectInteractionRef.current);
        // map?.removeInteraction(selectInteractionRef.current);
      }
    }

    // Initialize interaction on mount or when opts change
    initializeInteraction();

    // Cleanup function to remove interaction when component unmounts or opts change
    return () => {
      if (map) {
        if (selectInteractionRef.current) map.removeInteraction(selectInteractionRef.current);
        // selectInteractionRef.current = null;
      }
    };
  }, [map, opts]); // Only re-run if map or opts change

  const [features, setFeatures] = useState<Record<string | number, ol.Feature>>({});

  const select = useCallback((feature: ol.Feature) => {
    setFeatures((f) => ({ ...f, [feature.getId()]: feature }));
    if (selectInteractionRef.current) {
      selectInteractionRef.current.getFeatures().push(feature);
    }
  }, []);

  const deselect = useCallback((feature: ol.Feature) => {
    setFeatures((f) => {
      const newFeatures = { ...f };
      delete newFeatures[feature.getId()];
      return newFeatures;
    });
    if (selectInteractionRef.current) {
      selectInteractionRef.current.getFeatures().remove(feature);
    }
  }, []);

  const toggle = useCallback((feature: ol.Feature) => {
    setFeatures((f) => {
      const newFeatures = { ...f, [feature.getId()]: feature };
      if (feature.getId() in f) {
        if (selectInteractionRef.current) {
          selectInteractionRef.current.getFeatures().remove(feature);
        }
        delete newFeatures[feature.getId()];
        const { [feature.getId()]: _, ...remainingFeatures } = f;
        return remainingFeatures;
      } else {
        if (selectInteractionRef.current) {
          if (
            selectInteractionRef.current
              .getFeatures()
              .getArray()
              .find((a) => a.getId() === feature.getId())
          ) {
          } else {
            selectInteractionRef.current.getFeatures().push(feature);
          }
        }
        newFeatures[feature.getId()] = feature;
        return {
          ...f,
          [feature.getId()]: feature,
        };
      }
      // return newFeatures;
    });
  }, []);

  const clear = useCallback(() => {
    if (selectInteractionRef.current && selectInteractionRef.current.getFeatures()) {
      selectInteractionRef.current.getFeatures().clear();
    }
    setFeatures({});
  }, []);

  return { features, select, deselect, toggle, clear, length: Object.keys(features).length };
}

const InventoryMapListPage: FC = () => {
  const filterItem = useFilterItem();

  const map = useMap()!;
  const gpsPosition = useGPSPosition(map);
  const mapCenter = useMapCenter(map);
  const distanceFrom = gpsPosition || mapCenter;
  const sortSource = gpsPosition ? "GPS" : "MAP";

  const [filter, setFilter] = useState<string>("");
  const [haveMultiSelection, setHaveMultiSelection] = useState<boolean>(false);
  const [geometrySelectActive, setGeometrySelectActive] = useState<boolean>(false);

  const vectorRef = useRef<ol.layer.Vector | null>(null);
  const drawRef = useRef<ol.interaction.Draw | null>(null);
  const customStyleForSelected: Record<string, Style> = {
    strokeColor: {
      color: "#E03C31", // Fill color
      borderColor: "", // Border color
      form: "", // Form or shape
      formField: "", // A field name
      formFieldValue: "", // A field value
    },
    fillColor: {
      color: "#E03C31", // Fill color
      borderColor: "", // Border color
      form: "", // Form or shape
      formField: "", // A field name
      formFieldValue: "", // A field value
    },
  };
  const selectInteraction = useSelectionInteraction(map, {
    condition: ol.events.condition.click,
    style: mapUtils.getStyle(customStyleForSelected, undefined),
  });

  const updateStyle = useCallback(() => {
    const deselectedStyle = mapUtils.getDeselectedStyle;
    const inventoryLayers = mapUtils.getInventoryLayers(map) as ol.layer.Vector[];
    inventoryLayers.forEach((inventoryLayer) => {
      inventoryLayer.setStyle(deselectedStyle as ol.StyleFunction);
      const geomType = mapUtils.getGeometryType(inventoryLayer);

      const selectedStyle = mapUtils.getStyle(undefined, geomType);
      const style = selectInteraction.length > 0 ? deselectedStyle : selectedStyle;
      inventoryLayer.setStyle(style as ol.StyleFunction);
    });
  }, [map, selectInteraction.length]);

  const getFeatureProperties = useCallback(
    (getAll: boolean = false) => {
      const extent = map.getView().calculateExtent(map.getSize());

      const layers = mapUtils.getInventoryLayers(map);
      const features: ListFeature[] = [];
      layers.forEach((layer) => {
        const source = layer.getSource() as ol.source.Vector;
        let srcFeatures = getAll ? source.getFeatures() : source.getFeaturesInExtent(extent);
        srcFeatures.forEach((ft) => {
          if (layer.get("name") === "Inventory") {
            ft.get("features").forEach((feature: ol.Feature) => {
              const coord = (feature.getGeometry() as ol.geom.SimpleGeometry).getFirstCoordinate();
              features.push({
                id: feature.getId() as string,
                coord: coord,
                formdata: feature.get("formdata"),
                title: feature.get("title"),
                olFeature: feature,
              });
            });
          } else {
            const coord = (ft.getGeometry() as ol.geom.SimpleGeometry).getFirstCoordinate();
            features.push({
              id: ft.getId() as string,
              coord: coord,
              formdata: ft.get("formdata"),
              title: ft.get("title"),
              olFeature: ft,
            });
          }
        });
      });
      return features;
    },
    [map],
  );

  const features = getFeatureProperties();

  // we must re-render when the features in the map change
  const [_forceRender, forceRender] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const layers = mapUtils.getInventoryLayers(map);
    for (const layer of layers) {
      layer.getSource().on(["addfeature", "removefeature", "clear"], forceRender);
    }
    return () => {
      for (const layer of layers) {
        layer.getSource().un(["addfeature", "removefeature", "clear"], forceRender);
      }
    };
  }, [map]);

  const handleValueClick = useCallback(
    (feature: { id: string; olFeature: ol.Feature & { formdata?: any } }) => {
      selectInteraction.toggle(feature.olFeature);
    },
    [selectInteraction],
  );

  const setMapClickEnabled = useSetMapClickEnabled();
  const stopGeomSelection = useCallback(() => {
    setMapClickEnabled(true);
    if (drawRef.current !== null) {
      map.removeInteraction(drawRef.current);
      drawRef.current = null;
    }
    if (vectorRef.current !== null) {
      map.removeLayer(vectorRef.current);
      vectorRef.current = null;
    }
    setGeometrySelectActive(false);
  }, [map, setMapClickEnabled]);

  const handleGeomSelection = useCallback(() => {
    if (geometrySelectActive) {
      stopGeomSelection();
      return;
    }
    vectorRef.current = null;
    drawRef.current = null;
    const source = new ol.source.Vector({ wrapX: false });
    const vector = new ol.layer.Vector({
      source: source,
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: "rgba(190, 190, 255, 0.6)",
        }),
      }),
    });

    const draw = new ol.interaction.Draw({
      source: source,
      type: "Polygon",
    });

    map.addLayer(vector);
    vectorRef.current = vector;
    map.addInteraction(draw);
    drawRef.current = draw;
    setGeometrySelectActive(true);
    setMapClickEnabled(false);

    draw.on("drawend", (e: ol.interaction.Draw.Event) => {
      map.removeInteraction(draw);
      drawRef.current = null;
      map.removeLayer(vector);
      vectorRef.current = null;
      setMapClickEnabled(true);
      const inventoryLayers = mapUtils.getInventoryLayers(map);
      let needClear = true;
      inventoryLayers.forEach((layer) => {
        const invSource = layer.getSource();
        const mapFeatures = (invSource as ol.source.Vector).getFeatures();
        if (mapFeatures !== null && mapFeatures !== undefined && mapFeatures.length > 0) {
          if (needClear) {
            needClear = false;
            selectInteraction.clear();
          }

          const geomType = layer.get("geomType");
          mapFeatures
            .flatMap((feature) => (geomType === "Point" ? (feature.get("features") as ol.Feature[]) : [feature]))
            .filter((ft) => e.feature.getGeometry().intersectsCoordinate((ft.getGeometry() as ol.geom.Point).getCoordinates()))
            .forEach((ft) => {
              selectInteraction.select(ft);
            });
        }
      });
      if (!needClear) {
        setGeometrySelectActive(false);
      }
    });
  }, [geometrySelectActive, map, selectInteraction, setMapClickEnabled, stopGeomSelection]);

  const formDataRowFilter = useCallback((fItem: FilterItem, row: ListFeature["formdata"][number]) => {
    if (!(row && row.values && row.form === fItem.form.key)) {
      return false;
    }
    for (let i = 0; i < fItem.valueKeys.length; i++) {
      const key = fItem.valueKeys[i];
      const value = fItem.values[key];
      if (!(key in row.values)) {
        return false;
      }
      const field = fItem.form.fields[key];
      if (field.type === "dropdown" || field.type === "combobox") {
        // Require exact match for dropdown and combobox
        if (row.values[key].value !== value) {
          return false;
        }
      } else if (!compare(row.values[key].value, value)) {
        return false;
      }
    }
    return true;
  }, []);

  const formDataFilter = useCallback(
    (fItem: FilterItem, formData: ListFeature["formdata"]) => formData.some((row) => formDataRowFilter(fItem, row)),
    [formDataRowFilter],
  );

  const filterFeatures = useCallback(
    (features: ListFeature[]) => {
      if (filterItem) {
        return features.filter((feature) => formDataFilter(filterItem, feature.formdata));
      } else if (filter) {
        return features.filter((feature) => {
          const title = feature.title;
          const formData = feature.formdata;
          if (title && compare(title, filter)) {
            return true;
          }
          const matches = formData
            .filter((row) => row && row.values)
            .reduce((result, row) => {
              const values = row.values;
              Object.keys(values).forEach((key) => {
                result.push(values[key].value);
              });
              return result;
            }, [] as any[])
            .filter((value: any) => value && compare(value, filter));
          return !!matches.length;
        });
      } else {
        return features;
      }
    },
    [filter, filterItem, formDataFilter],
  );

  const handleFilterChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const filterValue = e.target.value;
    setFilter(filterValue);
  }, []);
  const handleFilterClear = useCallback(() => {
    setFilter("");
  }, []);

  const handleSelectionClear = useCallback(() => {
    setHaveMultiSelection(false);
    selectInteraction.clear();
    updateStyle();
  }, [selectInteraction, updateStyle]);

  const handleSelectionAll = useCallback(() => {
    //selectInteraction.clear();

    let featuresToUse = features;
    if (filterItem || filter) {
      // Have active filter so use all features to select all matching the filter.
      featuresToUse = getFeatureProperties(true);
    }

    const filtFeatures = filterFeatures(featuresToUse);
    filtFeatures.forEach((feature) => {
      selectInteraction.select(feature.olFeature);
    });
    updateStyle();
    setHaveMultiSelection(_.size(features) > 1);
  }, [features, filter, filterFeatures, filterItem, getFeatureProperties, selectInteraction, updateStyle]);
  useEffect(() => {
    vectorRef.current = null;
    drawRef.current = null;
    updateStyle();
    return () => {
      stopGeomSelection();
      updateStyle();
    };
  }, [getFeatureProperties, map, stopGeomSelection, updateStyle]);

  const css = {
    icon: {
      fontSize: 25,
      paddingLeft: 10,
      paddingRight: 10,
    },
  };

  const filteredFeatures = filterFeatures(features);

  const currentViewProjection = mapUtils.getProjection(map);

  const distanceFromWGS84 = ol.proj.transform(distanceFrom, currentViewProjection, "EPSG:4326");
  const wgs84Sphere = new ol.Sphere(6378137);
  const sortedFeatures = _.sortBy(
    filteredFeatures.map<ListFeature & { distance: number }>((feature: ListFeature) => {
      const transformedPoint = ol.proj.transform(feature.coord, currentViewProjection, "EPSG:4326");
      const distance = wgs84Sphere.haversineDistance(distanceFromWGS84, transformedPoint);
      return { ...feature, distance };
    }),
    "distance",
  );

  const featuresCountLimit = config.isApp ? 200 : Infinity;
  return (
    <div>
      <ListPanel>
        <ListFilter
          onChange={handleFilterChange}
          onClear={handleFilterClear}
          haveMultipleSelection={haveMultiSelection}
          onGeomSelectionClick={handleGeomSelection}
          filter={filter}
          geometrySelectActive={geometrySelectActive}
        />
        {filteredFeatures.length < featuresCountLimit ? (
          <ListTable
            onSelect={handleValueClick}
            iconAll={
              selectInteraction.length < 1 ? (
                <i className="fa fa-circle-thin" style={css.icon} onClick={handleSelectionAll} />
              ) : selectInteraction.length === _.size(filteredFeatures) ? (
                <i className="fa fa-circle" style={css.icon} onClick={handleSelectionClear} />
              ) : (
                <i className="fa fa-dot-circle-o" style={css.icon} onClick={handleSelectionClear} />
              )
            }
            sortSource={sortSource}
            features={sortedFeatures}
            selectedFeatures={selectInteraction.features}
          />
        ) : (
          <div style={{ textAlign: "center", maxWidth: 170, padding: 10 }} key="toManyMessage">
            <p>
              <strong>{selectInteraction.length ? selectInteraction.length + " selected " : features.length} items</strong>
            </p>
          </div>
        )}
      </ListPanel>
      {filteredFeatures.length < featuresCountLimit && selectInteraction.length > 0 ? (
        <ListImages selectedFeatures={selectInteraction.features} features={sortedFeatures} />
      ) : null}
    </div>
  );
};

export default InventoryMapListPage;
